import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface DriveNode {
  id: string;
  name: string;
  mimeType: string | null;
  isFolder: boolean;
  isImage: boolean;
  size: number | null;
  modifiedAt: string | null;
  iconLink: string | null;
  /** Path relative to the patient's Drive root folder (includes the name). */
  path: string;
  children?: DriveNode[];
}

export interface DriveListing {
  connected: boolean;
  hasFolder: boolean;
  folderId?: string;
  files: DriveNode[];
}

export interface DriveSyncResult {
  synced: number;
  total: number;
  results: Array<{ id: string; ok: boolean; name?: string; folder?: string; error?: string }>;
}

export function listDriveFiles(patientId: number): Promise<DriveListing> {
  return apiClient
    .get<ApiResponse<DriveListing>>(`/patients/${patientId}/drive`)
    .then((r) => r.data.data);
}

/** Fetch a Drive file's bytes for inline preview. Returns an object URL. */
export async function fetchDriveFileViewUrl(patientId: number, fileId: string): Promise<string | null> {
  try {
    const response = await apiClient.get(`/patients/${patientId}/drive/file`, {
      params: { fileId },
      responseType: 'blob',
    });
    const blob = response.data as Blob;
    if (blob.size === 0) return null;
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

export async function downloadDriveFile(patientId: number, fileId: string, name: string): Promise<void> {
  const response = await apiClient.get(`/patients/${patientId}/drive/file`, {
    params: { fileId },
    responseType: 'blob',
  });
  const blob = response.data as Blob;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Copy selected Drive files into the patient's EFS documents folder. */
export function syncDriveFiles(
  patientId: number,
  files: Array<{ id: string; folder: string }>,
): Promise<DriveSyncResult> {
  return apiClient
    .post<ApiResponse<DriveSyncResult>>(`/patients/${patientId}/drive/sync`, { files })
    .then((r) => r.data.data);
}
